"use client";

import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import { forwardRef, useState } from "react";
import type { InputProps } from "../types/ui.types";

// Simple className utility function
const cn = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

const InputField = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = "text",
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      required = false,
      icon,
      rightIcon,
      error,
      className,
      id,
      name,
      autoComplete,
      autoFocus = false,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    // Get default icon based on input type
    const getDefaultIcon = () => {
      switch (type) {
        case "email":
          return <Mail className="w-5 h-5" />;
        case "password":
          return <Lock className="w-5 h-5" />;
        case "text":
          return <User className="w-5 h-5" />;
        default:
          return null;
      }
    };

    // Get default right icon for password fields
    const getDefaultRightIcon = () => {
      if (type === "password") {
        return (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="w-5 h-5 text-black/50 hover:text-black/70 transition-colors cursor-pointer"
          >
            {showPassword ? (
              <Eye className="w-5 h-5" />
            ) : (
              <EyeOff className="w-5 h-5" />
            )}
          </button>
        );
      }
      return null;
    };

    const displayIcon = icon || getDefaultIcon();
    const displayRightIcon = rightIcon || getDefaultRightIcon();
    const inputType = type === "password" && showPassword ? "text" : type;

    const baseClasses = cn(
      // Base styling matching UI prototype exactly
      "w-full py-4 bg-white/40 border-0 rounded-md text-black placeholder-black/40",
      "focus:bg-white/60 focus:outline-none focus:ring-2 focus:ring-black/20",
      "transition-all duration-200",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      // Padding adjustments based on icons
      displayIcon && !displayRightIcon ? "pl-12 pr-4" : "",
      !displayIcon && displayRightIcon ? "pl-4 pr-12" : "",
      displayIcon && displayRightIcon ? "pl-12 pr-12" : "",
      !displayIcon && !displayRightIcon ? "px-4" : "",
      // Error state
      error ? "ring-2 ring-red-500/50 bg-red-50/40" : "",
      className,
    );

    return (
      <div className="relative">
        {/* Left Icon */}
        {displayIcon && (
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <div className="text-black/50 flex items-center">{displayIcon}</div>
          </div>
        )}

        {/* Input Field */}
        <input
          ref={ref}
          type={inputType}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          autoComplete={autoComplete}
          className={baseClasses}
          {...props}
        />

        {/* Right Icon */}
        {displayRightIcon && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
            <div className="text-black/50 hover:text-black/70 transition-colors cursor-pointer">
              {displayRightIcon}
            </div>
          </div>
        )}

        {/* Error styling is handled via ring classes in baseClasses */}
      </div>
    );
  },
);

InputField.displayName = "InputField";

export { InputField };
